import { useState, useEffect, useCallback } from "react";

type ShortcutGroup = {
  label: string;
  items: { keys: string; description: string }[];
};

const shortcutGroups: ShortcutGroup[] = [
  {
    label: "Navigation",
    items: [
      { keys: "G then D", description: "Go to Dashboard" },
      { keys: "G then L", description: "Go to Leads" },
      { keys: "G then P", description: "Go to Pipeline" },
      { keys: "G then C", description: "Go to Calls" },
      { keys: "G then W", description: "Go to WhatsApp" },
      { keys: "G then R", description: "Go to Reports" },
    ],
  },
  {
    label: "Actions",
    items: [
      { keys: "N", description: "New Lead" },
      { keys: "Ctrl+K", description: "Search" },
      { keys: "Escape", description: "Close modal / dropdown" },
      { keys: "?", description: "Show shortcuts" },
    ],
  },
  {
    label: "Leads",
    items: [
      { keys: "E", description: "Edit selected lead" },
      { keys: "Delete", description: "Delete (with confirm)" },
      { keys: "Arrow keys", description: "Navigate rows" },
      { keys: "Enter", description: "Open lead detail" },
    ],
  },
];

type Props = {
  onClose: () => void;
};

export default function KeyboardShortcutsModal({ onClose }: Props) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).dataset.backdrop === "true") {
        setVisible(false);
        setTimeout(onClose, 200);
      }
    },
    [onClose]
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setVisible(false);
        setTimeout(onClose, 200);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      data-backdrop="true"
      onClick={handleBackdropClick}
      className={`fixed inset-0 z-[60] flex items-center justify-center bg-black/40 transition-opacity duration-200 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        className={`bg-white rounded-xl w-full max-w-lg shadow-lg overflow-hidden transition-all duration-200 ${
          visible ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Keyboard Shortcuts</h2>
          <button
            onClick={() => {
              setVisible(false);
              setTimeout(onClose, 200);
            }}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Shortcut groups */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
          {shortcutGroups.map((group) => (
            <div key={group.label}>
              <h3 className="text-xs font-semibold uppercase text-gray-400 tracking-wider mb-3">
                {group.label}
              </h3>
              <ul className="space-y-2.5">
                {group.items.map((item) => (
                  <li key={item.keys} className="flex items-center justify-between gap-3">
                    <kbd className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-xs font-mono text-gray-700 flex-shrink-0">
                      {item.keys}
                    </kbd>
                    <span className="text-sm text-gray-600 text-right leading-tight">
                      {item.description}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">Press Escape to close</p>
        </div>
      </div>
    </div>
  );
}